import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Save, Download, RefreshCw, ExternalLink, ShieldOff, BookOpen } from 'lucide-react';
import { Input } from '@/components/ui/input';

const DEFAULTS = {
  setting_key: 'global',
  phone: '0151 528 3001',
  email: 'sales@motionreality.co.uk',
  website: 'motionreality.co.uk',
  company_name: 'Motion Reality Sim Racing Ltd',
  company_reg: '16171282',
  vat_reg: 'GB491158870',
  registered_office: '3rd Floor, 86-90 Paul Street, London, England, EC2A 4NE',
  venue_address: 'Unit 9, Croft Business Park\nCarrock Road\nBromborough\nWirral\nCH62 3RA',
  logo_url: '',
  bronze_price: '£595 + VAT',
  silver_price: '£1,195 + VAT',
  gold_price: '£2,250 + VAT',
  platinum_price: 'From £795 + VAT',
  cover_subtext: 'SIM RACING • BROMBOROUGH, WIRRAL',
  cover_eyebrow: '2026 / 2027 EVENT SPECIFICATION',
  cover_title: 'CORPORATE BROCHURE',
  cover_subtitle: 'Events • Team Building • Brand Activations',
  cover_tagline: 'Bridging the gap between virtual and real-world motorsport.',
  cta_text: 'Ready to secure your track date? Contact our events team today or request a custom proposal via motionreality.co.uk/corporate',
};

const textareaClass = 'w-full bg-[#111115] border border-white/10 text-white p-3 text-sm focus:border-primary/60 outline-none transition-colors resize-none';

export default function AdminCorporateBrochure() {
  const { user, isLoadingAuth } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState(DEFAULTS);
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [brochureUrl, setBrochureUrl] = useState('');

  useEffect(() => {
    if (isLoadingAuth) return;
    if (!user || user.role !== 'admin') return;
    loadData();
  }, [user, isLoadingAuth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const rows = await base44.entities.CorporateBrochureSettings.filter(
        { setting_key: 'global' }, '-updated_date', 1
      );
      if (rows && rows[0]) {
        setRecordId(rows[0].id);
        setForm({ ...DEFAULTS, ...rows[0] });
      }
      const media = await base44.entities.PageMedia.filter(
        { page_key: 'corporate', slot_key: 'brochure_pdf' }, '-updated_date', 1
      );
      if (media && media[0]) setBrochureUrl(media[0].image_url);
    } catch {
      // entity may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.created_date;
      delete payload.updated_date;
      delete payload.created_by_id;
      if (recordId) {
        await base44.entities.CorporateBrochureSettings.update(recordId, payload);
      } else {
        const created = await base44.entities.CorporateBrochureSettings.create(payload);
        setRecordId(created.id);
      }
      toast({ title: 'Settings saved', description: 'Rebuilding the brochure PDF from your changes…' });
      // Rebuild straight away so the stored PDF never lags behind the settings.
      await rebuildBrochure({ silentSuccess: true });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Builds the brochure from the saved settings and stores it as the live copy.
  // The download on /corporate always serves that stored file, so this is what
  // keeps the public brochure in step with the settings.
  const rebuildBrochure = async ({ silentSuccess = false } = {}) => {
    setGenerating(true);
    try {
      const res = await base44.functions.invoke('generateCorporateBrochure', {});
      if (res.data?.success) {
        setBrochureUrl(res.data.fileUrl);
        if (!silentSuccess) {
          toast({ title: 'Brochure rebuilt', description: 'The stored PDF now matches the current settings.' });
        }
      } else {
        toast({ title: 'Rebuild failed', description: res.data?.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Rebuild failed', description: e.message, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  if (isLoadingAuth || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3">
        <ShieldOff className="w-12 h-12 text-muted-foreground" />
        <p className="text-muted-foreground">Admin access required.</p>
      </div>
    );
  }

  const labelClass = 'block text-white/30 text-[10px] font-bold tracking-widest mb-1.5';
  const inputClass = 'bg-[#111115] border-white/10 text-white h-11 focus:border-primary/60';

  return (
    <div className="min-h-screen bg-[#0d0d0f] p-6 lg:p-10">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-primary/10 border border-primary/20">
            <BookOpen className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-1">B2B MARKETING</p>
            <h1 className="text-white font-black text-2xl uppercase">Corporate Brochure</h1>
          </div>
        </div>

        {/* Generate section */}
        <div className="border border-white/10 bg-[#111115] p-6 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white font-bold text-sm mb-1">Current Brochure</p>
              {brochureUrl ? (
                <p className="text-white/40 text-xs">A brochure has been generated and is live on the /corporate page.</p>
              ) : (
                <p className="text-white/40 text-xs">No brochure generated yet. Configure the fields below and click Generate.</p>
              )}
            </div>
            <button
              onClick={() => rebuildBrochure()}
              disabled={generating}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {brochureUrl ? 'REGENERATE' : 'GENERATE'}
            </button>
          </div>
          {brochureUrl && (
            <div className="mt-4 flex items-center gap-4 border-t border-white/5 pt-4">
              <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary text-xs font-bold hover:underline">
                <ExternalLink className="w-3.5 h-3.5" /> View / Download PDF
              </a>
            </div>
          )}
        </div>

        {/* Config form */}
        <div className="space-y-6">
          {/* Contact Details */}
          <div className="border border-white/10 bg-[#111115] p-6">
            <p className="text-primary text-[10px] font-bold tracking-widest mb-4">CONTACT DETAILS</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>PHONE</label>
                <Input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>EMAIL</label>
                <Input value={form.email || ''} onChange={(e) => set('email', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>WEBSITE</label>
                <Input value={form.website || ''} onChange={(e) => set('website', e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="border border-white/10 bg-[#111115] p-6">
            <p className="text-primary text-[10px] font-bold tracking-widest mb-4">COMPANY INFORMATION</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>COMPANY NAME</label>
                <Input value={form.company_name || ''} onChange={(e) => set('company_name', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>COMPANY REG NO</label>
                <Input value={form.company_reg || ''} onChange={(e) => set('company_reg', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>VAT REG NO</label>
                <Input value={form.vat_reg || ''} onChange={(e) => set('vat_reg', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>REGISTERED OFFICE</label>
                <Input value={form.registered_office || ''} onChange={(e) => set('registered_office', e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelClass}>VENUE ADDRESS (use line breaks)</label>
              <textarea value={form.venue_address || ''} onChange={(e) => set('venue_address', e.target.value)} rows={4} className={textareaClass} />
            </div>
            <div className="mt-4">
              <label className={labelClass}>LOGO URL (optional — defaults to Brand Asset)</label>
              <Input value={form.logo_url || ''} onChange={(e) => set('logo_url', e.target.value)} placeholder="Leave blank to use Brand Asset logo" className={inputClass} />
            </div>
          </div>

          {/* Package Pricing */}
          <div className="border border-white/10 bg-[#111115] p-6">
            <p className="text-primary text-[10px] font-bold tracking-widest mb-4">PACKAGE PRICING</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>BRONZE</label>
                <Input value={form.bronze_price || ''} onChange={(e) => set('bronze_price', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>SILVER</label>
                <Input value={form.silver_price || ''} onChange={(e) => set('silver_price', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>GOLD</label>
                <Input value={form.gold_price || ''} onChange={(e) => set('gold_price', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>PLATINUM</label>
                <Input value={form.platinum_price || ''} onChange={(e) => set('platinum_price', e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>

          {/* Cover Page Text */}
          <div className="border border-white/10 bg-[#111115] p-6">
            <p className="text-primary text-[10px] font-bold tracking-widest mb-4">COVER PAGE TEXT</p>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>SUB-TEXT (below logo)</label>
                <Input value={form.cover_subtext || ''} onChange={(e) => set('cover_subtext', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>EYEBROW TAG</label>
                <Input value={form.cover_eyebrow || ''} onChange={(e) => set('cover_eyebrow', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>TITLE</label>
                <Input value={form.cover_title || ''} onChange={(e) => set('cover_title', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>SUBTITLE</label>
                <Input value={form.cover_subtitle || ''} onChange={(e) => set('cover_subtitle', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>TAGLINE</label>
                <Input value={form.cover_tagline || ''} onChange={(e) => set('cover_tagline', e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="border border-white/10 bg-[#111115] p-6">
            <p className="text-primary text-[10px] font-bold tracking-widest mb-4">CALL TO ACTION</p>
            <label className={labelClass}>CTA TEXT</label>
            <textarea value={form.cta_text || ''} onChange={(e) => set('cta_text', e.target.value)} rows={3} className={textareaClass} />
          </div>

          {/* Save, with a manual rebuild as a secondary action */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              onClick={() => rebuildBrochure()}
              disabled={generating || saving}
              title="Force a re-render of the stored PDF, e.g. after changing a linked asset"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white/60 hover:text-white font-bold text-xs tracking-widest px-6 py-3.5 transition-colors disabled:opacity-50"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              REBUILD PDF
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold tracking-widest px-8 py-3.5 text-xs transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              SAVE CONFIGURATION
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}